import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Camera, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PlateCombobox } from "@/components/PlateCombobox";
import { AuthImage } from "@/components/damages/AuthImage";
import { api, errMsg } from "@/lib/api";

const NONE = "__none";
const blank = { toll_date: "", amount: "", vehicle_id: "", notice_number: "", company: "", entry_station: "", exit_station: "", driver_id: NONE, station_id: "", notes: "" };
const F = ({ label, children, span }) => <div className={`space-y-1.5 ${span ? "sm:col-span-2" : ""}`}><Label>{label}</Label>{children}</div>;

export const TollDialog = ({ item, stations, vehicles, isAdmin, onClose, onSaved }) => {
  const [f, setF] = useState(blank);
  const [photos, setPhotos] = useState([]);
  const [files, setFiles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (item) { setF({ ...blank, station_id: stations[0]?.id || "", ...item, driver_id: item.driver_id || NONE, amount: item.amount ?? "" }); setPhotos(item.photos || []); setFiles([]); }
  }, [item, stations]);
  const st = isAdmin ? f.station_id : vehicles[0]?.station_id;
  useEffect(() => {
    if (item && (st || !isAdmin)) api.get("/drivers", { params: { station_id: isAdmin ? st : undefined } }).then((r) => setDrivers(r.data)).catch(() => setDrivers([]));
  }, [item, st, isAdmin]);
  if (!item) return null;
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const vOpts = vehicles.filter((v) => !isAdmin || v.station_id === st);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...Object.fromEntries(Object.keys(blank).map((k) => [k, f[k]])), amount: Number(f.amount), driver_id: f.driver_id === NONE ? null : f.driver_id };
      const { data } = item.id ? await api.put(`/tolls/${item.id}`, payload) : await api.post("/tolls", payload);
      if (files.length) {
        const fd = new FormData();
        files.forEach((x) => fd.append("files", x));
        try { await api.post(`/tolls/${data.id}/photos`, fd); } catch (e) { toast.error(`Dati salvati, ma foto non caricate: ${errMsg(e)}`); }
      }
      toast.success("Mancato pedaggio salvato");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const delPhoto = async (p) => {
    try { await api.delete(`/tolls/photos/${p.id}`); setPhotos(photos.filter((x) => x.id !== p.id)); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-xl" data-testid="toll-dialog">
        <DialogHeader>
          <DialogTitle>{item.id ? "Modifica mancato pedaggio" : "Nuovo mancato pedaggio"}</DialogTitle>
          <DialogDescription>Le foto vengono salvate su Google Drive.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <F label="Data mancato pedaggio"><Input type="date" value={f.toll_date} onChange={set("toll_date")} data-testid="toll-date-input" /></F>
          <F label="Importo (€)"><Input type="number" step="0.01" min="0" value={f.amount} onChange={set("amount")} data-testid="toll-amount-input" /></F>
          {isAdmin && <F label="Stazione" span>
            <Select value={f.station_id || undefined} onValueChange={(v) => setF((p) => ({ ...p, station_id: v, vehicle_id: "", driver_id: NONE }))}>
              <SelectTrigger data-testid="toll-station-select"><SelectValue placeholder="Seleziona" /></SelectTrigger>
              <SelectContent>{stations.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </F>}
          <F label="Targa mezzo"><PlateCombobox value={f.vehicle_id} onChange={(v) => setF((p) => ({ ...p, vehicle_id: v }))} testId="toll-vehicle-select" options={vOpts.map((v) => ({ value: v.id, label: v.plate }))} /></F>
          <F label="Numero mancato pagamento"><Input value={f.notice_number} onChange={set("notice_number")} data-testid="toll-notice-input" /></F>
          <F label="Società autostradale" span><Input value={f.company} onChange={set("company")} placeholder="Es. Autostrade per l'Italia" list="toll-companies" data-testid="toll-company-input" />
            <datalist id="toll-companies">{["Autostrade per l'Italia", "Autostrada del Brennero", "Autostrade Lombarde", "Milano Serravalle - Milano Tangenziali", "Pedemontana Lombarda", "Brebemi", "Autostrada dei Fiori", "SATAP", "Strada dei Parchi", "Autovia Padana", "CAV - Concessioni Autostradali Venete", "Autostrade Alto Adriatico"].map((c) => <option key={c} value={c} />)}</datalist>
          </F>
          <F label="Stazione di entrata"><Input value={f.entry_station} onChange={set("entry_station")} data-testid="toll-entry-input" /></F>
          <F label="Stazione di uscita"><Input value={f.exit_station} onChange={set("exit_station")} data-testid="toll-exit-input" /></F>
          <F label="Driver" span>
            <PlateCombobox value={f.driver_id} onChange={(v) => setF((p) => ({ ...p, driver_id: v }))} testId="toll-driver-select" placeholder="Seleziona driver" searchPlaceholder="Cerca driver..."
              options={[{ value: NONE, label: "— Nessun driver" }, ...drivers.filter((d) => d.active || d.id === item.driver_id).map((d) => ({ value: d.id, label: d.name }))]} />
          </F>
          <F label="Note" span><Textarea rows={2} value={f.notes} onChange={set("notes")} data-testid="toll-notes-input" /></F>
          <F label="Foto del mancato pedaggio" span>
            <div className="grid grid-cols-3 gap-2">
              {photos.map((p) => (
                <div key={p.id} className="relative"><AuthImage photoId={p.id} base="/tolls/photos" className="h-24 w-full rounded-lg" testId={`toll-photo-${p.id}`} />
                  <button type="button" onClick={() => delPhoto(p)} className="absolute right-1 top-1 rounded-full bg-white/90 p-1" data-testid={`toll-photo-delete-${p.id}`}><X className="h-3 w-3" /></button></div>
              ))}
            </div>
            <label className="flex h-10 cursor-pointer items-center gap-2 rounded-md border border-dashed border-slate-300 px-3 text-sm text-slate-600 hover:border-slate-400">
              <Camera className="h-4 w-4" /><span className="truncate">{files.length ? `${files.length} foto da caricare` : "Aggiungi foto..."}</span>
              <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => setFiles([...e.target.files])} data-testid="toll-photo-input" />
            </label>
          </F>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={save} disabled={busy || !f.toll_date || !(Number(f.amount) > 0) || !f.vehicle_id} data-testid="toll-save-button">{busy ? "Salvataggio..." : "Salva"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
